import type { LangChainMessage } from '@assistant-ui/react-langgraph';
import { sanitizeMessagesForAssistantUi } from './messageUtils';

/**
 * Detect abort-like errors (DOMException AbortError, wrapped abort errors, etc.)
 * so stream handlers can silently swallow them.
 */
export function isAbortLikeError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;

  if (typeof err === 'object' && err !== null && 'name' in err) {
    const name = (err as { name?: unknown }).name;
    if (name === 'AbortError') return true;
  }

  if (err instanceof Error) {
    if (err.name === 'AbortError') return true;
    const message = err.message.toLowerCase();
    if (
      message === 'aborterror' ||
      message.includes('aborted') ||
      message.includes('cancelled') ||
      message.includes('canceled')
    )
      return true;
  }

  return false;
}

function extractErrorText(data: unknown): string | null {
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && data !== null) {
    if ('message' in data) return String((data as { message?: unknown }).message ?? '');
    if ('error' in data) return String((data as { error?: unknown }).error ?? '');
  }
  return null;
}

/**
 * Detect stream errors caused by intentional interruption/cancellation (e.g.
 * double-texting with multitaskStrategy="interrupt"), which should not be
 * surfaced as user-visible failures.
 */
export function isInterruptionLikeStreamError(data: unknown): boolean {
  const text = extractErrorText(data)?.toLowerCase();
  if (!text) return false;

  return (
    text.includes('abort') ||
    text.includes('cancel') ||
    text.includes('interrupted') ||
    text.includes('superseded') ||
    text.includes('multitask strategy') ||
    text.includes('another run') ||
    text.includes('an internal error occurred')
  );
}

type InterceptOpts = {
  threadId: string;
  extractFromValues: (values: unknown, threadId?: string) => void;
  setStreamingStatus: (threadId: string | null | undefined, status: string | null) => void;
};

const CONTEXT_TOO_LONG_MESSAGE =
  'The conversation exceeded the context length limit. ' +
  'Please start a new conversation and try rephrasing your question to be more specific or narrow the scope.';

const RECURSION_LIMIT_MESSAGE =
  'This question required too many steps to answer and hit the processing limit. ' +
  'Please start a new conversation and try breaking it into smaller, more specific questions ' +
  '— for example, ask about one metric or topic at a time.';

/**
 * Map known LangGraph SSE error types/messages to user-friendly text.
 * Returns null if the error is not recognized.
 */
function getFriendlyErrorMessage(data: Record<string, unknown>): string | null {
  const errorType = String(data.error ?? '').toLowerCase();
  const message = String(data.message ?? '').toLowerCase();

  const isContextError =
    errorType.includes('contextoverflowerror') ||
    message.includes('prompt is too long') ||
    message.includes('context_length_exceeded') ||
    message.includes('context length') ||
    message.includes('too many messages');

  if (isContextError) return CONTEXT_TOO_LONG_MESSAGE;

  const isRecursionError =
    errorType === 'graphrecursionerror' ||
    message.includes('recursion limit');

  if (isRecursionError) return RECURSION_LIMIT_MESSAGE;

  return null;
}

/**
 * Async generator that intercepts a LangGraph stream, extracting side-channel
 * data (files, assets, streaming status) while forwarding all events to the
 * caller (assistant-ui runtime).
 */
export async function* interceptStream(
  rawStream: AsyncIterable<{ event: string; data: unknown }>,
  opts: InterceptOpts,
) {
  const { threadId, extractFromValues, setStreamingStatus } = opts;
  try {
    for await (const event of rawStream) {
      if (event.event === 'error') {
        // Double-text interruptions can surface as stream-level errors from the
        // superseded run. Ignore these so the UI doesn't flash transient errors.
        if (isInterruptionLikeStreamError(event.data)) continue;

        // Rewrite known error types to user-friendly messages
        const data = event.data as Record<string, unknown> | undefined;
        if (data) {
          const friendly = getFriendlyErrorMessage(data);
          if (friendly) {
            yield { ...event, data: { ...data, message: friendly } };
            continue;
          }
        }
      } else if (event.event === 'values') {
        extractFromValues(event.data, threadId);
        const values = event.data as { messages?: LangChainMessage[] } | undefined;
        if (values?.messages) {
          yield {
            ...event,
            data: { ...values, messages: sanitizeMessagesForAssistantUi(values.messages) },
          };
          continue;
        }
      } else if (event.event === 'custom') {
        const data = event.data as { status?: string } | undefined;
        if (data && 'status' in data) {
          setStreamingStatus(threadId, data.status || null);
        }
        continue; // don't forward to assistant-ui
      }
      yield event;
    }
  } finally {
    // Always clear on completion, cancellation, or stream error.
    setStreamingStatus(threadId, null);
  }
}

/** Abort-aware sleep that rejects with AbortError when signalled. */
export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}
